import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ChevronDownIcon } from '../../../model/icons';
import { useConfirmationModalStore } from '../../../../../stores';
import SeqCard from './SeqCard';

// Строка участника (.lg-part): аккордеон с последовательностью точек, перемещением и сбросом.
export default function ParticipantRow({ participant, index, points, qrBaseUrl, legendsState, onReload }) {
    const { username, finished, finishOrder, rewardAmount, currentSequenceId } = participant;
    const [open, setOpen] = useState(false);
    const [sequences, setSequences] = useState([]);
    const [loading, setLoading] = useState(false);
    const { open: openConfirm } = useConfirmationModalStore();

    const loadSequences = async () => {
        setLoading(true);
        const data = await legendsState.getParticipantSequences(participant.id);
        setSequences((data || []).sort((a, b) => (a.orderId ?? 0) - (b.orderId ?? 0)));
        setLoading(false);
    };

    const toggle = async () => {
        if (!open && sequences.length === 0) await loadSequences();
        setOpen(!open);
    };

    // Перемещение/сброс меняют последовательность — обновляем кэш без ремаунта строки
    // (раньше это делал композитный key, из-за чего строка мигала).
    React.useEffect(() => {
        setSequences([]);
        if (open) loadSequences();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSequenceId, finished]);

    const currentPosition = currentSequenceId ?? 0;
    const steps = sequences.map(seq => {
        const point = points.find(p => p.id === seq.pointId) || {};
        const orderId = seq.orderId ?? 0;
        let status = 'lock';
        if (orderId < currentPosition || finished) status = 'done';
        else if (orderId === currentPosition && !finished) status = 'current';
        return { point, status };
    });
    const passed = steps.filter(s => s.status === 'done').length;

    const move = (position, pointName) => openConfirm(
        <div className="confirm-msg">
            <b>Переместить на точку?</b>
            <p>Участник «{username || 'Без имени'}» будет перемещён на точку #{position}{pointName ? ` «${pointName}»` : ''}.</p>
        </div>,
        async () => { await legendsState.moveParticipantToPosition(participant.id, position); await onReload(); },
        { confirmText: 'Переместить' }
    );

    const reset = () => openConfirm(
        <div className="confirm-msg">
            <b>Сбросить прогресс?</b>
            <p>Участник «{username || 'Без имени'}» начнёт квест заново. Последовательность, награда и место будут удалены.</p>
        </div>,
        async () => { await legendsState.resetParticipantProgress(participant.id); await onReload(); },
        { confirmText: 'Сбросить' }
    );

    return (
        <div className={`lg-part${open ? ' open' : ''}`}>
            <div className="lg-phead" role="button" tabIndex={0} onClick={toggle} onKeyDown={(e) => { if (e.key === 'Enter') toggle(); }}>
                <span className="lg-prank">{index + 1}</span>
                <span className="lg-puser">{username || 'Без имени'}</span>
                <span className="lg-pprogress">{finished ? points.length : currentPosition}/{points.length}</span>
                {finished ? (
                    <span className="lg-pbadge fin">#{finishOrder}{rewardAmount != null && <span className="rw">+{rewardAmount}</span>}</span>
                ) : (
                    <span className="lg-pbadge prog">В процессе</span>
                )}
                <span className="lg-chev"><ChevronDownIcon /></span>
            </div>
            {open && (
                <div className="lg-pbody">
                    {loading ? (
                        <div className="lg-pprog">Загрузка…</div>
                    ) : (
                        <>
                            <div className="lg-pprog">Пройдено: {passed} / {sequences.length} точек</div>
                            <div className="lg-seqgrid">
                                {steps.map((s, i) => (
                                    <SeqCard
                                        key={s.point.id ?? `pos-${i}`}
                                        point={s.point}
                                        index={i}
                                        qrBaseUrl={qrBaseUrl}
                                        status={s.status}
                                        onMove={s.status !== 'current' ? () => move(i + 1, s.point.name) : undefined}
                                    />
                                ))}
                            </div>
                            {sequences.length > 0 && (
                                <div className="lg-reset">
                                    <button type="button" className="btn btn-success" onClick={reset}>Сбросить прогресс</button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

ParticipantRow.propTypes = {
    participant: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    points: PropTypes.array.isRequired,
    qrBaseUrl: PropTypes.string,
    legendsState: PropTypes.object.isRequired,
    onReload: PropTypes.func.isRequired
};

ParticipantRow.defaultProps = { qrBaseUrl: null };
